import { useDataApi } from "@/hooks";
import {
  combinePaths,
  isFunction,
  isNonEmptyArray,
  isNonEmptyObject,
  isNonEmptyString,
  isStringEqualCI
} from "@/utils";
import {
  GridEventListener,
  GridFilterModel,
  GridPaginationModel,
  GridRowEditStopReasons,
  GridRowId,
  GridRowModel,
  GridRowModes,
  GridRowModesModel,
  GridSortItem,
  GridSortModel,
  GridValidRowModel,
  useGridApiRef
} from "@mui/x-data-grid";
import { cloneDeep, delay } from "lodash";
import {
  useEffect,
  useImperativeHandle,
  useRef,
  useState
} from "react";
import { firstPage, initialPaginationModel, pageSize } from "./constants";
import { CrudGridProps } from "./interface";


function useCrudGrid(props: CrudGridProps) {

  const {
    baseUrl,
    path,
    additionalURLSearchParams,
    getRowId,
    setRowId,
    newRecordId = -1,
    initialState,
    search: _search,
    onEditRow,
    setTotalRowCount: _setTotalRowCount,
    checkboxSelection,
    sortModel,
  } = props

  const { data, isLoading, error, setUrl, post, put, _delete, response } =
    useDataApi(undefined);
  const [rows, setRows] = useState<readonly GridValidRowModel[]>([]);
  const previousUrl = useRef<string | undefined>(undefined);
  const [totalRowCount, setTotalRowCount] = useState(0);
  const [paginationModel, setPaginationModel] = useState(
    initialPaginationModel
  );
  const [search, setSearch] = useState<string | undefined>(_search);
  const [rowModesModel, setRowModesModel] = useState<GridRowModesModel>({});
  const [sortingModel, setSortingModel] = useState(
    initialState?.sorting?.sortModel?.[0]
  );
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [itemToDelete, setItemToDelete] = useState<any>(undefined);
  const apiRef = useGridApiRef();
  const [searchInput, setSearchInput] = useState(_search || "");

  const deleteItemToDelete = () => {
    if (!itemToDelete) {
      return;
    }

    const id = getRowId?.(itemToDelete) || itemToDelete.id;
    const _rows = rows.filter((row) => (getRowId?.(row) || row.id) !== id);
    setRows(_rows);

    if (isFunction(_setTotalRowCount)) {
      _setTotalRowCount?.(_rows.length || 0);
    }

    setItemToDelete(undefined);
  };

  const reloadData = () => {
    fetchData(paginationModel.page + 1, search, sortingModel, true);
  };

  const deleteSelectedRows = () => {
    var selectedRows = apiRef.current.getSelectedRows();
    if (selectedRows.size === 0) {
      return;
    }

    setIsDeleteDialogOpen(true);
  };


  const fetchData = (
    page: number,
    search?: string,
    sortItem?: GridSortItem,
    isReload?: boolean
  ): void => {
    const searchParams = new URLSearchParams();

    if (isNonEmptyObject(additionalURLSearchParams)) {
      const keys = Object.keys(additionalURLSearchParams!);
      for (let i = 0, l = keys.length; i < l; ++i) {
        searchParams.append(keys[i], additionalURLSearchParams![keys[i]]);
      }
    }

    searchParams.append("pageNum", page.toString());
    searchParams.append("pageSize", pageSize.toString());

    if (isNonEmptyString(search)) {
      searchParams.append("search", search!);
    }

    if (isNonEmptyString(sortItem?.field)) {
      searchParams.append("sortingCol", sortItem!.field);
    }
    if (isNonEmptyString(sortItem?.sort as string)) {
      searchParams.append("sortType", sortItem!.sort!);
    }

    if (isReload) {
      searchParams.append("timestamp", new Date().valueOf().toString());
    }

    let _newUrl = baseUrl;
    if (isNonEmptyString(path)) {
      _newUrl = combinePaths(_newUrl, path);
    }

    const newUrl = `${_newUrl}?${searchParams.toString()}`;

    if (previousUrl.current === newUrl) {
      return;
    }

    previousUrl.current = newUrl;
    setUrl(newUrl);
  };

  useEffect(() => {
    fetchData(paginationModel.page + 1, search, sortingModel);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    onSearchChange(_search);
  }, [_search]);

  useEffect(() => {
    if (isLoading || !data) {
      return;
    }
    setRows(data.data || []);
    setTotalRowCount(data.totalCount || 0);

    if (isFunction(_setTotalRowCount)) {
      _setTotalRowCount?.(data.totalCount || 0);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, data]);

  useEffect(() => {
    if (error) {
      return;
    }

    if (isStringEqualCI(response?.config?.method, "delete")) {
      if (itemToDelete) {
        deleteItemToDelete();
      }

      return;
    }

    if (!response?.data) {
      return;
    }

    const newId = getRowId?.(response.data) || response.data.id;
    const _rows = cloneDeep(rows);
    const newRecord = _rows.find(
      (row) => (getRowId?.(row) || row.id) === newRecordId
    );
    if (newRecord) {
      setRowId?.(newRecord, newId);
      setRows(_rows);
    }
  }, [error, response]);

  const onPaginationModelChange = (model: GridPaginationModel) => {
    setPaginationModel(model);
    fetchData(model.page + 1, search, sortingModel);
  };

  const onFilterModelChange = (model: GridFilterModel) => {
    if (!isNonEmptyArray(model.items)) {
      return;
    }

    onSearchChange(model.items[0].value);
  };

  const onSearchChange = (value?: string) => {
    setRows([]);
    setPaginationModel(initialPaginationModel);
    setSearch(value);
    fetchData(firstPage, value, sortingModel);
  };

  const onSortModelChange = (model: GridSortModel) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if (!isNonEmptyArray(model as any[])) {
      return;
    }

    setSortingModel(model[0]);
    fetchData(paginationModel.page + 1, search, model[0]);
  };

  const handleEditClick = (id: GridRowId) => () => {
    if (isFunction(onEditRow)) {
      onEditRow?.(rows.find((row) => (getRowId?.(row) || row.id) === id));
      return;
    }

    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } });
  };

  const handleSaveClick = (id: GridRowId) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } });
  };

  const handleDeleteClick = (id: GridRowId) => () => {
    const item = rows.find((row) => (getRowId?.(row) || row.id) === id);
    if (!item) {
      return;
    }

    setItemToDelete(item);
    setIsDeleteDialogOpen(true);
  };

  const handleCancelClick = (id: GridRowId) => () => {
    setRowModesModel({
      ...rowModesModel,
      [id]: { mode: GridRowModes.View, ignoreModifications: true },
    });

    const editedRow = rows.find((row) => (getRowId?.(row) || row.id) === id);
    if (editedRow!.isNew) {
      setRows(rows.filter((row) => (getRowId?.(row) || row.id) !== id));
    }
  };

  const processRowUpdate = (newRow: GridRowModel) => {
    const updatedRow = { ...newRow, isNew: false };
    const id = getRowId?.(newRow) || newRow.id;

    setRows(
      rows.map((row) =>
        (getRowId?.(row) || row.id) === id ? updatedRow : row
      )
    );

    if (id === newRecordId) {
      post(`${baseUrl}`, newRow);
    } else {
      put(`${baseUrl}/${id}`, newRow);
    }

    return updatedRow;
  };

  const handleRowModesModelChange = (newRowModesModel: GridRowModesModel) => {
    setRowModesModel(newRowModesModel);
  };

  const handleRowEditStop: GridEventListener<"rowEditStop"> = (
    params,
    event
  ) => {
    if (params.reason === GridRowEditStopReasons.rowFocusOut) {
      event.defaultMuiPrevented = true;
    }
  };

  const handleDeleteDialogClosed = (result?: boolean) => {
    setIsDeleteDialogOpen(false);

    if (!result) {
      return;
    }

    const ids = [];

    if (checkboxSelection) {
      const selectedRows = apiRef.current.getSelectedRows();
      if (selectedRows.size === 0) {
        return;
      }

      selectedRows.forEach((row) => {
        ids.push(getRowId?.(row) || row.id);
      });
    } else {
      if (!itemToDelete) {
        return;
      }

      ids.push(getRowId?.(itemToDelete) || itemToDelete.id);
    }

    for (let i = 0, l = ids.length; i < l; ++i) {
      _delete(`${baseUrl}/${ids[i]}`);
    }

    if (checkboxSelection) {
      delay(reloadData, 1000);
    }
  };

  // Keep sortingModel in sync with external sortModel prop
  useEffect(() => {
    if (Array.isArray(sortModel) && sortModel.length > 0) {
      setSortingModel(sortModel[0]);
      fetchData(paginationModel.page + 1, search, sortModel[0]);
    } else if (Array.isArray(sortModel) && sortModel.length === 0) {
      setSortingModel(undefined);
      fetchData(paginationModel.page + 1, search, undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(sortModel)]);

  return {
    rowModesModel, rows, handleSaveClick,
    handleCancelClick,
    handleEditClick,
    handleDeleteClick,
    totalRowCount,
    isDeleteDialogOpen,
    searchInput,
    setSearchInput,
    onPaginationModelChange,
    onFilterModelChange,
    onSortModelChange,
    processRowUpdate,
    handleRowModesModelChange,
    handleRowEditStop,
    handleDeleteDialogClosed,
    reloadData,
    deleteSelectedRows,
    onSearchChange,
    apiRef,
    isLoading,
    paginationModel,
    setRows,
    setRowModesModel,
    error
  }
}

export default useCrudGrid